import { createSlice, type PayloadAction, createSelector } from '@reduxjs/toolkit';
import type { OrderByCode } from '@/services/reparoly/ordersApi';
import type { RootState } from '@/app/store/store';
import {
  normalizeOrdersPayload,
  type IntakeGroup,
  type NormalizedOrderItem,
  type OrdersPayloadNode,
} from '@/lib/orders/normalizeOrders';


export type SearchState = {
  query: string;
  result: NormalizedOrderItem | OrderByCode | null;
  items: NormalizedOrderItem[];
  groups: Array<IntakeGroup | NormalizedOrderItem>;
  refreshOnMount: boolean;
};

const initialState: SearchState = {
  query: '',
  result: null,
  items: [],
  groups: [],
  refreshOnMount: false,
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setQuery(state, action: PayloadAction<string>) {
      state.query = action.payload;
    },
    setResult(state, action: PayloadAction<NormalizedOrderItem | OrderByCode | null>) {
      state.result = action.payload;
    },
    setItems(state, action: PayloadAction<OrdersPayloadNode[]>) {
      const { items, groups } = normalizeOrdersPayload(action.payload as OrdersPayloadNode[]);
      state.items = items;
      state.groups = groups;
    },
    setRefreshOnMount(state, action: PayloadAction<boolean>) {
      state.refreshOnMount = action.payload;
    },
    updateItem(state, action: PayloadAction<{ id: number; changes: Partial<NormalizedOrderItem> }>) {
      const { id, changes } = action.payload;

      state.items = state.items.map((it) => (it.id === id ? { ...it, ...changes } : it));
      state.groups = state.groups.map((node) => {
        if ('items' in node) {
          return {
            ...node,
            items: node.items.map((it) => (it.id === id ? { ...it, ...changes } : it)),
          };
        }
        return node.id === id ? { ...node, ...changes } : node;
      });

      if (state.result && 'id' in state.result && state.result.id === id) {
        state.result = { ...state.result, ...changes } as NormalizedOrderItem | OrderByCode;
      }
    },
    resetSearch() {
      return { ...initialState };
    },
  },
});

export const { setQuery, setResult, setItems, setRefreshOnMount, updateItem, resetSearch } =
  searchSlice.actions;
export default searchSlice.reducer;

export const selectSearch = (state: RootState) => state.search;
export const selectItems = (state: RootState) => state.search.items;
export const selectGroups = (state: RootState) => state.search.groups;

export const makeSelectGroupByIntakeId = (intakeId: number) =>
  createSelector(selectGroups, (groups) =>
    groups.find((node): node is IntakeGroup => 'items' in node && node.intake_id === intakeId),
  );

export const makeSelectOrderById = (id: number) =>
  createSelector(selectItems, (items) => items.find((it) => it.id === id));
